import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";

import PageHero from "@/components/PageHero";
import Seo from "@/components/Seo";
import { SERVICES } from "@/lib/site";

const HERO_IMG =
  "/images/9ae3kktf.png";

const PROCESS = [
  {
    step: "01",
    title: "Site Assessment",
    body: "Substrate testing, moisture readings, profile measurement and exposure-condition review.",
  },
  {
    step: "02",
    title: "System Specification",
    body: "Written scope with manufacturer system, mil thickness, topcoat and detailing drawings.",
  },
  {
    step: "03",
    title: "Surface Preparation",
    body: "In-house shot blasting, grinding, scarifying and patching to manufacturer profile spec.",
  },
  {
    step: "04",
    title: "Installation",
    body: "Certified applicators install in phases with containment, vapor control and QC checks.",
  },
  {
    step: "05",
    title: "Handover & Warranty",
    body: "Final walk, maintenance docs, and manufacturer system warranty up to 10 years.",
  },
];

export default function Services() {
  return (
    <div data-testid="page-services">
      <Seo
        title="Services"
        description="Industrial resinous, residential epoxy chip-flake, food-grade urethane and exterior stucco systems — installed end-to-end by Vision Era crews across the GTA."
        path="/services"
      />
      <PageHero
        eyebrow="Capabilities"
        title="Four systems. One contractor."
        subtitle="Specialty surfaces engineered for industrial duty cycles, hygienic compliance, and architectural performance — installed end-to-end by our own crews."
        image={HERO_IMG}
      />

      <section className="section-pad">
        <div className="container-max space-y-10">
          {SERVICES.map((s, idx) => (
            <article
              key={s.slug}
              data-testid={`service-detail-${s.slug}`}
              className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center ${
                idx % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
              }`}
            >
              <div className="lg:col-span-6">
                <div
                  className="aspect-[4/3] bg-cover bg-center border border-zinc-800"
                  style={{ backgroundImage: `url(${s.image})` }}
                />
              </div>
              <div className="lg:col-span-6">
                <div className="eyebrow">0{idx + 1} · {s.short}</div>
                <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-wider text-zinc-50 mt-4 leading-[0.95]">
                  {s.title}
                </h2>
                <p className="text-zinc-400 mt-6 leading-relaxed text-lg">
                  {s.blurb}
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-8">
                  {s.features.map((f) => (
                    <li
                      key={f}
                      className="flex items-start gap-3 text-zinc-300"
                    >
                      <Check className="w-5 h-5 text-[#FF5A00] shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  data-testid={`service-cta-${s.slug}`}
                  className="btn-secondary mt-10"
                >
                  Quote this system <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section
        className="section-pad bg-zinc-900 border-y border-zinc-800"
        data-testid="process"
      >
        <div className="container-max">
          <div className="eyebrow">Our Process</div>
          <h2 className="font-display text-5xl lg:text-6xl uppercase tracking-wider text-zinc-50 mt-4 max-w-3xl">
            Five steps from bid
            <br />
            to handover.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-zinc-800 mt-14 border border-zinc-800">
            {PROCESS.map((p) => (
              <div key={p.step} className="bg-zinc-950 p-8">
                <div className="font-display text-5xl text-[#FF5A00] tracking-wider">
                  {p.step}
                </div>
                <h3 className="font-heading text-lg uppercase tracking-wider text-zinc-50 mt-4">
                  {p.title}
                </h3>
                <p className="text-zinc-400 mt-3 text-sm leading-relaxed">
                  {p.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-max text-center">
          <h2 className="font-display text-5xl lg:text-7xl uppercase tracking-wider text-zinc-50">
            Not sure which system fits?
          </h2>
          <p className="text-zinc-400 mt-6 max-w-2xl mx-auto text-lg">
            Tell us about your facility. We&apos;ll match the right system —
            and won&apos;t over-spec what you don&apos;t need.
          </p>
          <Link to="/contact" data-testid="services-final-cta" className="btn-primary mt-10">
            Talk to an Estimator <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
